import React, { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IHomeLayoutResponse, IHomeLayoutSection } from '@/interfaces/HomeLayout/homeLayout.interface';
import { Eye, EyeOff, Save } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { showToastNotification } from '@/utility/helper';
import { SectionCard } from '@/components/common/SectionCard';
import DragDropList from '@/components/common/DragDropList';


export const HomeLayout = () => {
    const [sections, setSections] = useState<IHomeLayoutSection[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchLayout = async () => {
        setLoading(true);
        try {
            const response = await callApi<IHomeLayoutResponse>(
                `${API_URL}${API_ENDPOINT.homeLayout}`,
                { method: 'GET' }
            );
            const sorted = (response?.data || []).sort((a, b) => a.rank - b.rank);
            setSections(sorted);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLayout();
    }, []);

    const handleReorder = (updatedItems: IHomeLayoutSection[]) => {
        const withRanks = updatedItems.map((item, index) => ({
            ...item,
            rank: index + 1
        }));
        setSections(withRanks);
    };

    const toggleVisibility = (id: string, enabled: boolean) => {
        setSections(prev => prev.map(s =>
            s.id === id ? { ...s, isEnabled: enabled } : s
        ));
    };

    const handleTitleChange = (id: string, newTitle: string) => {
        setSections(prev => prev.map(s =>
            s.id === id ? { ...s, title: newTitle } : s
        ));
    };

    const saveLayout = async () => {
        setSaving(true);
        try {
            const payload = {
                sections: sections.map(({ key, title, isEnabled, rank }) => ({
                    key,
                    title,
                    isEnabled,
                    rank
                }))
            };

            await callApi(
                `${API_URL}${API_ENDPOINT.homeLayout}`,
                {
                    method: 'PUT',
                    data: payload
                }
            );
            showToastNotification('success', 'Home layout updated successfully');
        } catch (error) {
            ThrowError(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6 pb-20">
                <MainHeading
                    title="Home Layout (CMS)"
                    description="Drag to reorder, rename titles, and toggle visibility of home screen sections."
                    actionLabel={saving ? 'Saving...' : 'Save Layout'}
                    actionIcon={<Save className="w-4 h-4" />}
                    onAction={saveLayout}
                    isActionDisabled={saving || loading || sections.length === 0}
                />

                <SectionCard title="Mobile App Structure">
                    <DragDropList
                        data={sections}
                        isLoading={loading}
                        getId={(item) => item.id}
                        onReorder={handleReorder}
                        header={
                            <div className="grid grid-cols-12 gap-4 text-[11px] font-black uppercase tracking-widest text-muted-foreground/70">
                                <div className="col-span-4 text-left">Display Title (Editable)</div>
                                <div className="col-span-3 text-left">Key</div>
                                <div className="col-span-2 text-left">View Type</div>
                                <div className="col-span-1 text-center">Rank</div>
                                <div className="col-span-2 text-right">Actions</div>
                            </div>
                        }
                        renderItem={(section) => (
                            <div className="grid grid-cols-12 items-center w-full py-1 gap-4 text-left">
                                <div className="col-span-4 min-w-0 pr-4">
                                    <input
                                        type="text"
                                        value={section.title}
                                        onChange={(e) => handleTitleChange(section.id, e.target.value)}
                                        className="w-full bg-secondary/5 border border-secondary/20 rounded-xl px-4 py-2 text-[13px] font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all placeholder:text-muted-foreground/30 shadow-inner"
                                        placeholder="Enter section title..."
                                    />
                                </div>

                                <div className="col-span-3">
                                    <span className="text-[11px] text-muted-foreground/60 font-mono bg-secondary/20 px-2 py-0.5 rounded uppercase tracking-wider truncate block w-fit">
                                        {section.key}
                                    </span>
                                </div>

                                <div className="col-span-2">
                                    <span className="text-[10px] bg-primary/5 text-primary/80 px-2 py-0.5 rounded font-black uppercase tracking-tight border border-primary/10 w-fit block">
                                        {section.viewType}
                                    </span>
                                </div>

                                <div className="col-span-1 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <span className="text-[9px] uppercase font-bold text-muted-foreground/40 leading-none mb-1">Rank</span>
                                        <span className="text-sm font-black text-primary/90 leading-none">{section.rank}</span>
                                    </div>
                                </div>

                                <div className="col-span-2 flex items-center justify-end gap-5">
                                    <div className="flex flex-col items-center">
                                        <span className="text-[9px] font-bold uppercase tracking-tighter text-muted-foreground mb-1">
                                            {section.isEnabled ? 'Visible' : 'Hidden'}
                                        </span>
                                        <Switch
                                            checked={section.isEnabled}
                                            onCheckedChange={(val) => toggleVisibility(section.id, val)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    />
                </SectionCard>
            </div>
        </Layout>
    );
};
